import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", email: "", password: "", division: "", shift: "" };

const sel = "px-2 py-2 rounded-lg border border-sage-200 bg-white/70 text-sm";

const AssignSelects = ({ value, onChange, config, prefix }) => (
  <div className="flex gap-2">
    <select value={value.division} onChange={(e) => onChange({ ...value, division: e.target.value })} className={sel} data-testid={`${prefix}-division-select`}>
      <option value="">Divisi…</option>
      {value.division && !config.divisions.includes(value.division) && <option value={value.division}>{value.division} (lama)</option>}
      {config.divisions.map((d) => <option key={d} value={d}>{d}</option>)}
    </select>
    <select value={value.shift} onChange={(e) => onChange({ ...value, shift: e.target.value })} className={sel} data-testid={`${prefix}-shift-select`}>
      <option value="">Shift…</option>
      {config.shifts.map((s) => <option key={s.id} value={s.id}>{s.label} {s.start}–{s.end}</option>)}
    </select>
  </div>
);

export const StaffAccounts = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [config, setConfig] = useState({ divisions: [], shifts: [] });

  useEffect(() => {
    api.get("/attendance/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const assign = async (s, v) => {
    try {
      await api.put(`/admin/staff/${s.id}`, { name: s.name, division: v.division, shift: v.shift });
      toast.success(`Divisi & shift ${s.name} disimpan`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const load = useCallback(() => {
    api.get("/admin/staff").then((r) => setList(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/staff", f);
      toast.success("Akun staf dibuat");
      setF(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus akun ${s.name}?`)) return;
    await api.delete(`/admin/staff/${s.id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Kata sandi diperbarui");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const input = "w-full px-3 py-2.5 rounded-lg border border-sage-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";
  return (
    <div className="space-y-6">
      <div className="card-paper p-6" data-testid="staff-accounts-panel">
        <h2 className="font-serif font-semibold text-2xl">Akun staf & karyawan</h2>
        <p className="text-xs text-muted2 mt-1">Atur divisi dan shift tiap karyawan untuk absensi.</p>
        <div className="divide-y divide-sage-200 mt-3">
          {list.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm" data-testid={`staff-account-${s.id}`}>
              <div><div className="font-semibold">{s.name}</div><div className="text-xs text-muted2">{s.email} · {s.role === "admin" ? "Admin" : "Staf"}</div></div>
              <div className="flex items-center gap-1">
                <AssignSelects value={{ division: s.division || "", shift: s.shift || "" }} onChange={(v) => assign(s, v)} config={config} prefix={`staff-${s.id}`} />
                {s.id !== user.id && s.role !== "admin" && (
                  <button onClick={() => remove(s)} className="p-2 rounded-lg hover:bg-red-50 text-red-700" data-testid={`staff-delete-button-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={add} className="grid gap-2 mt-4">
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nama staf" className={input} data-testid="staff-new-name-input" />
          <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={input} data-testid="staff-new-email-input" />
          <input required minLength={6} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Kata sandi (min. 6 karakter)" className={input} data-testid="staff-new-password-input" />
          <AssignSelects value={f} onChange={setF} config={config} prefix="staff-new" />
          <button className="btn-forest py-2.5 text-sm" data-testid="staff-add-button">Tambah staf</button>
        </form>
      </div>
      <form onSubmit={changePw} className="card-paper p-6 grid gap-2" data-testid="change-password-form">
        <h3 className="font-semibold">Ganti kata sandi saya</h3>
        <input required type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Kata sandi lama" className={input} data-testid="change-password-current-input" />
        <input required minLength={6} type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Kata sandi baru" className={input} data-testid="change-password-new-input" />
        <button className="px-4 py-2.5 rounded-lg border border-forest text-forest text-sm font-semibold hover:bg-sage-100" data-testid="change-password-submit">Simpan kata sandi</button>
      </form>
    </div>
  );
};
